import { Box, Button, TextInput, Upload } from "@bcc-ui/ui-components";
import { Form, Formik } from "formik";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { object } from "yup";

import { useAbortControllers } from "components/file-sharing/useAbortControllers";
import SelectingView from "components/import-file-dialog/SelectingView";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import style from "components/settings/report-signature-keys/report-signature-keys.scss";
import { NAME_MAX_LENGTH } from "domain/globalConstants";
import { createSignatureKeyNameSchema, ReportSignatureKeyResult } from "domain/reportSignatureKeys";
import { reportSignatureKeyService } from "services/report/erasure/ReportSignatureKeyService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import form from "styles/form.scss";
import { resolveColor } from "utils/commonFunctions";

import testIds from "testIds.json";

interface FormValues {
    name: string;
}

const ERROR_CODE_TRANSLATION_KEYS: Record<string, string> = {
    NAME_ALREADY_EXISTS: "ReportSignatureKeys.addForm.name.alreadyExistsException",
    INVALID_NAME: "ReportSignatureKeys.addForm.name.lengthException",
    INVALID_PEM: "ReportSignatureKeys.addForm.key.invalidPem",
    NOT_RSA_KEY: "ReportSignatureKeys.addForm.key.notRsaKey",
    UNSUPPORTED_KEY_SIZE: "ReportSignatureKeys.addForm.key.unsupportedKeySize",
    FINGERPRINT_ALREADY_EXISTS: "ReportSignatureKeys.addForm.key.fingerprintAlreadyExists",
    LIMIT_REACHED: "ReportSignatureKeys.addForm.limitReached",
};

interface Props {
    existingNames: string[];
    showResult: (result: ReportSignatureKeyResult, succeeded: boolean) => void;
}

export const deriveNameFromFileName = (uploadedFileName: string): string => {
    const nameWithoutExtension = uploadedFileName.replace(/\.[^./]+$/, "");
    return nameWithoutExtension !== "" ? nameWithoutExtension : uploadedFileName;
};

const AddReportSignatureKeyDialog = (props: Props) => {
    const { t } = useTranslation();
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const abortControllers = useAbortControllers();

    const [formVisible, setFormVisible] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [keyContent, setKeyContent] = React.useState<string | undefined>(undefined);
    const [fileName, setFileName] = React.useState<string | undefined>(undefined);
    const [fileError, setFileError] = React.useState<string | undefined>(undefined);

    const openForm = () => setFormVisible(true);
    const closeForm = () => {
        abortControllers.abortAll();
        setFormVisible(false);
        setKeyContent(undefined);
        setFileName(undefined);
        setFileError(undefined);
    };

    const onSelectFileClicked = () => fileInputRef.current?.click();

    const submitKey = (values: FormValues) => {
        // Submit button stays disabled while no file is selected, so this only guards TypeScript's narrowing.
        if (keyContent == null) {
            return;
        }
        const abortController = new AbortController();
        abortControllers.push(abortController);
        setLoading(true);
        usageStatisticsService.sendEvent({
            category: Category.REPORT_SIGNATURE_KEY,
            action: Action.ADD_REPORT_SIGNATURE_KEY,
        });
        reportSignatureKeyService
            .createKey(values.name.trim(), keyContent, abortController)
            .then(() => {
                props.showResult(
                    {
                        title: t("ReportSignatureKeys.addForm.success.title"),
                        message: t("ReportSignatureKeys.addForm.success.message", { name: values.name.trim() }),
                    },
                    true
                );
            })
            .catch((error) => {
                if (abortController.signal.aborted) {
                    return;
                }
                const message = mapErrorToMessage(error);
                props.showResult({ title: t("ReportSignatureKeys.addForm.failed.title"), message }, false);
            })
            .finally(() => {
                setLoading(false);
                closeForm();
            });
    };

    const mapErrorToMessage = (error: Error): string => {
        try {
            const code = JSON.parse(error.message)?.error?.code;
            const translationKey = code != null ? ERROR_CODE_TRANSLATION_KEYS[code] : undefined;
            return translationKey != null
                ? t(translationKey, { length: NAME_MAX_LENGTH })
                : t("ReportSignatureKeys.addForm.failed.message");
        } catch {
            return t("ReportSignatureKeys.addForm.failed.message");
        }
    };

    return (
        <div>
            <Button
                variant={"Tertiary"}
                type={"button"}
                icon={<Upload color={resolveColor("--theme-primary")} />}
                onClick={openForm}
                testId={testIds.workArea.settings.shared.reportSignatureKeys.addSignatureKeyButton}
            >
                {t("ReportSignatureKeys.addForm.button")}
            </Button>

            <Modal isOpen={formVisible} hideModal={closeForm} modalTitle={t("ReportSignatureKeys.addForm.title")}>
                {loading ? (
                    <LoadingIndicator />
                ) : (
                    <Formik<FormValues>
                        initialValues={{ name: "" }}
                        onSubmit={submitKey}
                        validationSchema={object().shape({
                            name: createSignatureKeyNameSchema(t, props.existingNames),
                        })}
                    >
                        {(formikProps) => {
                            const onNameDerived = (derivedName: string) =>
                                formikProps.setFieldValue("name", derivedName);

                            const readFile = (file: File) => {
                                const reader = new FileReader();
                                reader.readAsText(file);
                                reader.onload = () => {
                                    if (typeof reader.result === "string") {
                                        setKeyContent(reader.result);
                                        setFileName(file.name);
                                        setFileError(undefined);
                                        onNameDerived(deriveNameFromFileName(file.name));
                                    }
                                };
                                reader.onerror = () => {
                                    setKeyContent(undefined);
                                    setFileName(undefined);
                                    setFileError(t("ReportSignatureKeys.addForm.key.readFailed"));
                                };
                            };

                            const handleFileDrop = (fileList: FileList) => {
                                if (fileList.length > 0) {
                                    readFile(fileList[0]);
                                }
                            };

                            const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                                if (event.target.files != null && event.target.files.length > 0) {
                                    readFile(event.target.files[0]);
                                }
                            };

                            return (
                                <Form>
                                    <Box columnLayout gap={"Md"}>
                                        <TextInput
                                            label={t("ReportSignatureKeys.addForm.name.label")}
                                            name={"name"}
                                            id={"name"}
                                            maxLength={NAME_MAX_LENGTH}
                                            value={formikProps.values.name}
                                            onChange={formikProps.handleChange}
                                            error={formikProps.errors.name}
                                            testIds={{
                                                input: testIds.workArea.settings.shared.reportSignatureKeys
                                                    .addSignatureKeyDialog.nameInput.itself,
                                                error: testIds.workArea.settings.shared.reportSignatureKeys
                                                    .addSignatureKeyDialog.nameInput.errorLabel,
                                            }}
                                        />
                                        <Box columnLayout gap={"Xxs"}>
                                            <label className={style.fieldLabel}>
                                                {t("ReportSignatureKeys.addForm.key.label")}
                                            </label>
                                            <SelectingView
                                                handleFileDrop={handleFileDrop}
                                                onFileInputChange={onFileInputChange}
                                                onSelectFileClicked={onSelectFileClicked}
                                                fileInputRef={fileInputRef}
                                                multiple={false}
                                            />
                                            {fileName != null && (
                                                <div className={style.selectedFileMessage}>
                                                    {t("ReportSignatureKeys.addForm.key.selectedFile", { fileName })}
                                                </div>
                                            )}
                                            {fileError != null && <div className={form.error}>{fileError}</div>}
                                        </Box>
                                    </Box>
                                    <Box gap={"Sm"} horizontalAlignment={"Right"} marginTop={"Md"}>
                                        <Button
                                            type={"submit"}
                                            variant={"Primary"}
                                            disabled={
                                                formikProps.values.name.trim() === "" ||
                                                keyContent == null ||
                                                formikProps.errors.name != null
                                            }
                                            testId={testIds.common.dialog.primaryButton}
                                        >
                                            {t("Common.save")}
                                        </Button>
                                        <Button
                                            type={"button"}
                                            variant={"Secondary"}
                                            onClick={closeForm}
                                            testId={testIds.common.dialog.closeButton}
                                        >
                                            {t("Common.cancel")}
                                        </Button>
                                    </Box>
                                </Form>
                            );
                        }}
                    </Formik>
                )}
            </Modal>
        </div>
    );
};

export default AddReportSignatureKeyDialog;
